import React, { useState } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Target, Brain, ArrowRight, Star, AlertTriangle,
  Calculator, FileText, ChevronDown, ChevronUp,
  PenTool, RotateCcw, TrendingUp, Award, Youtube
} from 'lucide-react'
import { useProgressStore } from '../store/progressStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import { useVideoStore } from '../store/videoStore'

interface Step { label: string; content: string }

interface Problem {
  id: string
  level: 'basic' | 'standard' | 'advanced' | 'final'
  number: number
  tag: string
  question: string
  choices?: string[]
  answer: string
  solutionSteps: Step[]
  keyFormula?: string
  tip?: string
  concept: string
}

const PROBLEMS: Problem[] = [
  // ── 기본 문제 ────────────────────────────────────────────────────────────────
  {
    id: 'FP01', level: 'basic', number: 1,
    tag: '일차방정식·직선 변환', concept: '일차방정식과 직선',
    question: 'x + 2y − 6 = 0 을 y = ax + b 꼴로 나타낼 때, 기울기 a와 y절편 b를 구하시오.',
    answer: 'a = −1/2, b = 3',
    solutionSteps: [
      { label: '① y 항 정리', content: '2y = −x + 6' },
      { label: '② 양변 ÷ 2', content: 'y = −(1/2)x + 3' },
      { label: '③ 결론', content: '기울기 a = −1/2, y절편 b = 3' },
    ],
    keyFormula: 'ax + by + c = 0  →  y = −(a/b)x − (c/b)',
    tip: 'y 항을 좌변에 두고 나머지를 이항한 뒤 계수로 나눕니다.',
  },
  {
    id: 'FP02', level: 'basic', number: 2,
    tag: '두 직선 위치 관계', concept: '두 직선의 위치 관계',
    question: '두 직선 y = 3x + 1 과 y = 3x − 4 의 위치 관계를 고르시오.\n① 교차  ② 평행  ③ 일치',
    answer: '② 평행',
    solutionSteps: [
      { label: '① 기울기 비교', content: '3 = 3 (같음)' },
      { label: '② y절편 비교', content: '1 ≠ −4 (다름)' },
      { label: '③ 결론', content: '기울기 같고, y절편 다름 → 평행' },
    ],
    keyFormula: '기울기 같고 y절편 다름 → 평행',
    tip: '기울기 먼저, y절편 나중에 비교합니다.',
  },
  {
    id: 'FP03', level: 'basic', number: 3,
    tag: '연립방정식 교점', concept: '연립방정식과 교점',
    question: 'y = 2x + 1 과 y = −x + 7 의 교점을 구하시오.',
    answer: '(2, 5)',
    solutionSteps: [
      { label: '① 두 식 같다고 놓기', content: '2x + 1 = −x + 7' },
      { label: '② x 계산', content: '3x = 6 → x = 2' },
      { label: '③ y 계산', content: 'y = 2(2) + 1 = 5' },
      { label: '④ 검증', content: '−x+7: −2+7=5 ✓  →  교점 (2, 5)' },
    ],
    keyFormula: '두 y값 같다고 → 방정식 → x → y',
    tip: '구한 교점을 두 식 모두에 대입하여 검증합니다.',
  },
  {
    id: 'FP04', level: 'basic', number: 4,
    tag: '일치 판별', concept: '해의 특수한 경우',
    question: '{ 2x + y = 5, 4x + 2y = 10 의 해의 종류는?',
    answer: '무수히 많은 해 (두 직선 일치)',
    solutionSteps: [
      { label: '① 두 번째 식 ÷ 2', content: '2x + y = 5 → 첫 번째 식과 동일' },
      { label: '② 판별', content: '두 식이 같음 → 같은 직선' },
      { label: '③ 결론', content: '무수히 많은 해 (부정)' },
    ],
    keyFormula: '모든 계수 비율 같음 → 일치 → 무수히 많은 해',
    tip: '두 번째 식을 배수로 나눠 첫 번째와 비교합니다.',
  },
  {
    id: 'FP05', level: 'basic', number: 5,
    tag: '교점과 방정식 관계', concept: '연립방정식과 교점',
    question: 'x + y = 6 과 2x − y = 3 의 교점을 가감법으로 구하시오.',
    answer: '(3, 3)',
    solutionSteps: [
      { label: '① 두 식 더하기', content: '(x+y) + (2x−y) = 6+3 → 3x = 9' },
      { label: '② x 계산', content: 'x = 3' },
      { label: '③ y 계산', content: '3 + y = 6 → y = 3' },
      { label: '④ 검증', content: '2(3)−3 = 3 ✓  →  (3, 3)' },
    ],
    keyFormula: '가감법: 두 식을 더하거나 빼서 미지수 소거',
    tip: 'y의 계수가 부호가 반대이므로 더하면 소거됩니다.',
  },
  // ── 실력 문제 ────────────────────────────────────────────────────────────────
  {
    id: 'FP06', level: 'standard', number: 6,
    tag: '일반형→y=ax+b', concept: '일차방정식과 직선',
    question: '3x − 4y + 8 = 0 의 x절편과 y절편을 각각 구하시오.',
    answer: 'x절편: −8/3,  y절편: 2',
    solutionSteps: [
      { label: '① y절편 (x=0 대입)', content: '−4y + 8 = 0 → y = 2  →  y절편 = 2' },
      { label: '② x절편 (y=0 대입)', content: '3x + 8 = 0 → x = −8/3  →  x절편 = −8/3' },
    ],
    keyFormula: 'x절편: y=0 대입 / y절편: x=0 대입',
    tip: '절편 구하기는 다른 변수를 0으로 만들면 됩니다.',
  },
  {
    id: 'FP07', level: 'standard', number: 7,
    tag: '평행 조건', concept: '두 직선의 위치 관계',
    question: '직선 y = kx + 2 가 y = 4x − 1 과 평행하면 k의 값과, 이때 두 직선의 y절편 차이는?',
    answer: 'k = 4,  y절편 차이 = 3',
    solutionSteps: [
      { label: '① 평행 조건', content: '기울기 같음 → k = 4' },
      { label: '② y절편 확인', content: 'y절편: 2 vs −1 → 2 ≠ −1 → 평행 확인' },
      { label: '③ 차이', content: '2 − (−1) = 3' },
    ],
    keyFormula: '평행 → 기울기 같음, y절편 다름',
    tip: '평행 조건을 먼저 충족하고, y절편이 달라야 진짜 평행입니다.',
  },
  {
    id: 'FP08', level: 'standard', number: 8,
    tag: '그래프로 연립방정식 풀기', concept: '그래프 풀이',
    question: '{ x − y + 2 = 0, 2x + y − 8 = 0 을 그래프를 이용해 풀어라.',
    answer: '(2, 4)',
    solutionSteps: [
      { label: '① 각 직선 변환', content: '①: y = x + 2  ②: y = −2x + 8' },
      { label: '② 교점 계산', content: 'x + 2 = −2x + 8 → 3x = 6 → x = 2' },
      { label: '③ y 계산', content: 'y = 2 + 2 = 4' },
      { label: '④ 검증', content: '2−4+2=0 ✓, 4+4−8=0 ✓  →  (2, 4)' },
    ],
    keyFormula: '각 방정식 y=ax+b 변환 → 교점 좌표 = 해',
    tip: 'ax+by+c=0 꼴은 먼저 y=ax+b로 변환합니다.',
  },
  {
    id: 'FP09', level: 'standard', number: 9,
    tag: '계수 조건', concept: '해의 종류 판별',
    question: '{ ax + 2y = 6, 3x + y = 9 가 해가 없으려면 a의 값은?',
    answer: 'a = 6',
    solutionSteps: [
      { label: '① 해 없음 조건', content: 'a/3 = 2/1 ≠ 6/9' },
      { label: '② a/3 = 2 계산', content: 'a = 6' },
      { label: '③ 상수 비율 확인', content: '6/9 = 2/3 ≠ 2 → 평행 조건 만족 ✓' },
    ],
    keyFormula: '해 없음: a₁/a₂ = b₁/b₂ ≠ c₁/c₂',
    tip: '계수 비율이 같고 상수 비율이 다르면 평행 → 해 없음.',
  },
  {
    id: 'FP10', level: 'standard', number: 10,
    tag: '실생활 연립방정식', concept: '활용',
    question: '두 수의 합이 14이고 큰 수가 작은 수의 2배보다 1 작으면, 두 수를 구하시오.',
    answer: '큰 수 9, 작은 수 5',
    solutionSteps: [
      { label: '① 미지수 설정', content: '큰 수 x, 작은 수 y' },
      { label: '② 연립방정식', content: 'x + y = 14  ①\nx = 2y − 1  ②' },
      { label: '③ 대입', content: '(2y−1) + y = 14 → 3y = 15 → y = 5, x = 9' },
    ],
    keyFormula: '두 수 합·배수 조건 → 연립방정식 설정',
    tip: '"~배보다 ~만큼 크다/작다"는 부등호가 아닌 방정식입니다.',
  },
  // ── 심화 문제 ────────────────────────────────────────────────────────────────
  {
    id: 'FP11', level: 'advanced', number: 11,
    tag: '수직 조건', concept: '미지수 포함 직선',
    question: '직선 y = kx − 3 이 직선 y = (1/2)x + 1 에 수직일 때 k의 값은?',
    answer: 'k = −2',
    solutionSteps: [
      { label: '① 수직 조건', content: '기울기의 곱 = −1' },
      { label: '② 계산', content: 'k × (1/2) = −1 → k = −2' },
      { label: '③ 확인', content: '(−2) × (1/2) = −1 ✓' },
    ],
    keyFormula: '수직: a₁ × a₂ = −1',
    tip: '수직이면 기울기의 곱이 −1입니다. 합이 아닙니다!',
  },
  {
    id: 'FP12', level: 'advanced', number: 12,
    tag: '무수히 많은 해 조건', concept: '해의 종류',
    question: '{ 2x + ay = 4, bx + 3y = 6 이 무수히 많은 해를 가질 때 a + b의 값은?',
    answer: 'a + b = 4',
    solutionSteps: [
      { label: '① 무수히 많은 해 조건', content: '2/b = a/3 = 4/6 = 2/3' },
      { label: '② b 계산', content: '2/b = 2/3 → b = 3' },
      { label: '③ a 계산', content: 'a/3 = 2/3 → a = 2 (하지만 이렇게 하면 4/6=2/3 체크)\n실제: 2/b=a/3=4/6 → b=3, a=2' },
      { label: '④ a+b', content: '2 + 3 = 5' },
    ],
    keyFormula: '무수히 많은 해: a₁/a₂ = b₁/b₂ = c₁/c₂',
    tip: '세 비율이 모두 같아야 합니다. 빠짐없이 확인!',
  },
  {
    id: 'FP13', level: 'advanced', number: 13,
    tag: '세 직선 한 점에서 만남', concept: '교점 활용',
    question: '두 직선 y = x + 2 와 y = −2x + 5 의 교점을 직선 ax + y = 7 도 지날 때 a의 값은?',
    answer: 'a = 2',
    solutionSteps: [
      { label: '① 두 직선의 교점', content: 'x+2 = −2x+5 → 3x=3 → x=1, y=3 → (1, 3)' },
      { label: '② 교점을 세 번째 직선에 대입', content: 'a(1) + 3 = 7 → a = 4' },
    ],
    keyFormula: '세 직선 한 점 → 교점을 나머지 직선에 대입',
    tip: '먼저 두 직선의 교점을 구하고, 그 점을 세 번째 직선에 대입합니다.',
  },
  {
    id: 'FP14', level: 'advanced', number: 14,
    tag: '연립방정식 활용', concept: '실생활 활용',
    question: '어른 3명과 어린이 2명의 입장료 합이 28,000원이고,\n어른 1명과 어린이 4명의 입장료 합이 20,000원일 때,\n어른과 어린이 각각의 입장료를 구하시오.',
    answer: '어른: 8,000원, 어린이: 2,000원',
    solutionSteps: [
      { label: '① 미지수 설정', content: '어른 x원, 어린이 y원' },
      { label: '② 연립방정식', content: '3x + 2y = 28000  ①\nx + 4y = 20000  ②' },
      { label: '③ ①−②×3', content: '3x+2y−3x−12y = 28000−60000 → −10y = −32000 → y = 3200' },
      { label: '④ 재계산', content: '②: x + 12800 = 20000 → x = 7200\n검증: 3(7200)+2(3200)=21600+6400=28000 ✓' },
    ],
    keyFormula: '가격·개수 → 총액 방정식 2개 설정',
    tip: '가감법에서 소거할 미지수를 정하고 계수를 맞춥니다.',
  },
  {
    id: 'FP15', level: 'advanced', number: 15,
    tag: '기울기·절편으로 직선 결정', concept: '직선 결정',
    question: '직선이 x절편 = 4, y절편 = −2 일 때 이 직선의 방정식을 ax + by + c = 0 꼴로 나타내시오.',
    answer: 'x − 2y − 4 = 0',
    solutionSteps: [
      { label: '① 두 점 확인', content: 'x절편: (4, 0)  y절편: (0, −2)' },
      { label: '② 기울기', content: 'a = (−2−0)/(0−4) = −2/−4 = 1/2' },
      { label: '③ 함수식', content: 'y = (1/2)x − 2' },
      { label: '④ 일반형 변환', content: '×2: 2y = x − 4 → x − 2y − 4 = 0' },
    ],
    keyFormula: 'x절편(k,0)·y절편(0,m) → 기울기 = m/−k = −m/k',
    tip: '두 절편을 점으로 보고 기울기를 구합니다.',
  },
  // ── 종합 문제 ────────────────────────────────────────────────────────────────
  {
    id: 'FP16', level: 'final', number: 16,
    tag: '종합', concept: '일차방정식·직선 종합',
    question: '직선 2x − 3y + 6 = 0 에 대해 다음을 구하시오.\n① 기울기와 y절편\n② x절편\n③ 이 직선에 평행하고 점 (0, 1)을 지나는 직선의 방정식',
    answer: '① 기울기 2/3, y절편 2\n② x절편 −3\n③ y = (2/3)x + 1 → 2x − 3y + 3 = 0',
    solutionSteps: [
      { label: '① y=ax+b 변환', content: '−3y = −2x − 6 → y = (2/3)x + 2\n기울기 2/3, y절편 2' },
      { label: '② x절편 (y=0)', content: '2x + 6 = 0 → x = −3' },
      { label: '③ 평행 직선', content: '기울기 2/3, y절편 1 → y=(2/3)x+1 → 2x−3y+3=0' },
    ],
    keyFormula: '평행: 기울기 같음, y절편 다름',
    tip: '① 변환 → ② 대입 → ③ 평행 조건 순으로 풉니다.',
  },
  {
    id: 'FP17', level: 'final', number: 17,
    tag: '종합', concept: '연립방정식 해 판별',
    question: '연립방정식 { (k+1)x + 2y = 4, 3x + ky = 6 이\n① 유일한 해를 가질 조건\n② 해가 없을 조건\n③ 무수히 많은 해를 가질 조건을 각각 구하시오.',
    answer: '계수비율 조건: (k+1)/3 = 2/k\n→ k(k+1) = 6 → k = 2 또는 k = −3\n① 유일한 해: k ≠ 2 이고 k ≠ −3\n② 해 없음: 없음 (k=2,−3 모두 일치)\n③ 무수히 많은 해: k = 2 또는 k = −3',
    solutionSteps: [
      { label: '① 계수 비율 설정', content: '(k+1)/3 = 2/k (a비율=b비율)' },
      { label: '② 교차 곱', content: 'k(k+1) = 6 → k²+k−6 = 0 → (k+3)(k−2) = 0 → k=2 또는 k=−3' },
      { label: '③ c비율 확인', content: 'k=2: 4/6=2/3, a비율=3/3=1 ≠ 2/3? 재계산:\nk=2: (k+1)=3, a비율=3/3=1, b비율=2/2=1 → 같음, c비율=4/6=2/3 ≠ 1 → 해 없음(평행)\nk=−3: (k+1)=−2, a비율=−2/3, b비율=2/(−3)=−2/3 → 같음, c비율=4/6=2/3 ≠ −2/3 → 해 없음' },
      { label: '④ 결론', content: '① 유일한 해: k ≠ 2, k ≠ −3\n② 해 없음: k = 2 또는 k = −3\n③ 무수히 많은 해: 없음' },
    ],
    keyFormula: '비율 비교: a₁/a₂, b₁/b₂, c₁/c₂ 세 쌍 모두',
    tip: '미지수 k를 포함한 계수 비율 방정식을 풀고 각 경우의 c비율을 반드시 확인합니다.',
  },
  {
    id: 'FP18', level: 'final', number: 18,
    tag: '종합', concept: '직선과 삼각형 넓이',
    question: '두 직선 y = x + 2 와 y = −(1/2)x + 5, 그리고 y축으로 이루어진 삼각형의 넓이를 구하시오.',
    answer: '넓이 = 9/2 = 4.5',
    solutionSteps: [
      { label: '① 두 직선의 교점', content: 'x+2=−(1/2)x+5 → (3/2)x=3 → x=2, y=4 → (2,4)' },
      { label: '② y축과의 교점', content: 'y=x+2: (0,2)  /  y=−(1/2)x+5: (0,5)' },
      { label: '③ 삼각형 꼭짓점', content: '(0,2), (0,5), (2,4)' },
      { label: '④ 넓이 계산', content: 'y축 위 두 점 간격 = 5−2=3 (밑변), 높이=x방향 거리=2\n넓이 = (1/2)×3×2 = 3' },
    ],
    keyFormula: '삼각형 넓이 = (1/2) × 밑변 × 높이',
    tip: 'y축 위 두 점이 밑변, 교점의 x좌표가 높이입니다.',
  },
  {
    id: 'FP19', level: 'final', number: 19,
    tag: '종합', concept: '연립방정식 활용·속력',
    question: '두 도시 A, B 사이 거리는 120km이다.\n갑이 A에서 B로 시속 v₁km, 을이 B에서 A로 시속 v₂km로 동시에 출발하여 2시간 후에 만났다.\n또한 갑이 을보다 시속 10km 빠르다면, v₁과 v₂를 구하시오.',
    answer: 'v₁ = 35km/h,  v₂ = 25km/h',
    solutionSteps: [
      { label: '① 연립방정식 설정', content: '2v₁ + 2v₂ = 120  →  v₁ + v₂ = 60  ①\nv₁ − v₂ = 10  ②' },
      { label: '② 가감법', content: '①+②: 2v₁ = 70 → v₁ = 35' },
      { label: '③ v₂', content: 'v₂ = 60 − 35 = 25' },
      { label: '④ 검증', content: '2(35)+2(25)=70+50=120 ✓, 35−25=10 ✓' },
    ],
    keyFormula: '거리=속력×시간, 마주오면 두 거리의 합=전체 거리',
    tip: '"마주 보고 출발"이면 두 이동 거리의 합 = 전체 거리.',
  },
  {
    id: 'FP20', level: 'final', number: 20,
    tag: '최고난도', concept: '종합',
    question: '세 직선 ax + y = 2, x + by = 4, x + y = c 가 한 점에서 만날 때,\n그 교점이 (1, 1) 이면 a, b, c의 값을 각각 구하시오.',
    answer: 'a = 1, b = 3, c = 2',
    solutionSteps: [
      { label: '① 교점 (1,1) 첫 번째 식에 대입', content: 'a(1) + 1 = 2 → a = 1' },
      { label: '② 교점 (1,1) 두 번째 식에 대입', content: '1 + b(1) = 4 → b = 3' },
      { label: '③ 교점 (1,1) 세 번째 식에 대입', content: '1 + 1 = c → c = 2' },
      { label: '④ 검증', content: '①: 1+1=2 ✓  ②: 1+3=4 ✓  ③: 1+1=2 ✓' },
    ],
    keyFormula: '교점을 각 직선의 방정식에 대입 → 미지수 결정',
    tip: '교점이 주어지면 바로 각 방정식에 대입하면 됩니다.',
  },
]

const LEVEL_CONFIG = {
  basic:    { label: '기본', color: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200' },
  standard: { label: '실력', color: 'bg-blue-100 text-blue-700', border: 'border-blue-200' },
  advanced: { label: '심화', color: 'bg-orange-100 text-orange-700', border: 'border-orange-200' },
  final:    { label: '종합', color: 'bg-red-100 text-red-700', border: 'border-red-200' },
}

function ProblemCard({ prob }: { prob: Problem }) {
  const [open, setOpen] = useState(false)
  const [showSolution, setShowSolution] = useState(false)
  const lv = LEVEL_CONFIG[prob.level]
  return (
    <div className={`border-2 ${lv.border} rounded-2xl overflow-hidden`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors"
      >
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-sm flex-shrink-0 ${lv.color}`}>
          {prob.number}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${lv.color}`}>{lv.label}</span>
            <span className="text-xs text-slate-400 font-mono">{prob.tag}</span>
          </div>
          <p className="text-sm font-semibold text-slate-800 truncate">{prob.question.split('\n')[0]}</p>
        </div>
        {open ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          <p className="text-sm text-slate-800 whitespace-pre-line leading-relaxed">{prob.question}</p>

          {prob.keyFormula && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2">
              <p className="text-xs font-bold text-indigo-600 mb-0.5">📐 핵심 공식</p>
              <p className="text-xs font-mono text-indigo-800">{prob.keyFormula}</p>
            </div>
          )}
          {prob.tip && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              <p className="text-xs font-bold text-amber-600 mb-0.5">💡 풀이 팁</p>
              <p className="text-xs text-amber-800">{prob.tip}</p>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => setShowSolution(s => !s)}
              className="flex-1 py-2 rounded-xl text-xs font-bold bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors flex items-center justify-center gap-1"
            >
              <PenTool size={12} /> {showSolution ? '풀이 접기' : '단계별 풀이 보기'}
            </button>
          </div>

          {showSolution && (
            <div className="space-y-2">
              {prob.solutionSteps.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <div className="w-5 h-5 bg-blue-500 text-white rounded-full text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-600 mb-0.5">{s.label}</p>
                    <div className="bg-white rounded-lg px-2 py-1.5 border border-blue-100">
                      <p className="text-xs text-slate-700 whitespace-pre-line">{s.content}</p>
                    </div>
                  </div>
                </div>
              ))}
              <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl px-3 py-2.5">
                <p className="text-xs font-bold text-emerald-600 mb-1">✅ 정답</p>
                <p className="text-sm font-bold text-emerald-800 whitespace-pre-line">{prob.answer}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function F2_2ProblemPage() {
  const [filter, setFilter] = useState<'all' | 'basic' | 'standard' | 'advanced' | 'final'>('all')

  const filtered = filter === 'all' ? PROBLEMS : PROBLEMS.filter(p => p.level === filter)

  const counts = {
    basic: PROBLEMS.filter(p => p.level === 'basic').length,
    standard: PROBLEMS.filter(p => p.level === 'standard').length,
    advanced: PROBLEMS.filter(p => p.level === 'advanced').length,
    final: PROBLEMS.filter(p => p.level === 'final').length,
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <FileText size={24} className="text-indigo-500" />
          문제풀이
        </h1>
        <p className="text-sm text-slate-500 mt-1">일차함수와 일차방정식 · 20문제 (기본→실력→심화→종합)</p>
      </div>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap mb-6">
        {(['all', 'basic', 'standard', 'advanced', 'final'] as const).map(lv => (
          <button
            key={lv}
            onClick={() => setFilter(lv)}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${
              filter === lv ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {lv === 'all' ? `전체 (${PROBLEMS.length})` :
             lv === 'basic' ? `기본 (${counts.basic})` :
             lv === 'standard' ? `실력 (${counts.standard})` :
             lv === 'advanced' ? `심화 (${counts.advanced})` : `종합 (${counts.final})`}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map(prob => <ProblemCard key={prob.id} prob={prob} />)}
      </div>
    </div>
  )
}
